import type { ImportColumn } from '@paintscout/api'

/**
 * Field keys whose sample text we inspect for a packed multi-field value, and what a flagged column
 * of that field seems to pack together (named in the row tooltip).
 */
export const COMBINED_HINT_PARTS: Record<string, string> = {
  address: 'street, city, state and zip',
  city: 'city, state and zip',
  province: 'city, state and zip',
  postal: 'city, state and zip',
  first_name: 'a full name',
  last_name: 'a full name'
}

/** Derived from `COMBINED_HINT_PARTS`, so every inspected field always has tooltip parts. */
export const COMBINED_HINT_FIELDS = Object.keys(COMBINED_HINT_PARTS)

/** An Address sample with at least this many commas probably holds more than one field. */
export const COMBINED_HINT_MIN_COMMAS = 2

/** Fields where a "Portland, OR 97201"-style value means city, state and zip share one column. */
const LOCALITY_FIELDS = ['city', 'province', 'postal']

/** A two-letter state / province code ("OR", "AB") as its own word. */
const REGION_CODE = /\b[A-Z]{2}\b/

/** A US zip ("97201"; also matches the start of "97201-1234"). */
const US_ZIP = /\b\d{5}\b/

/** A Canadian postal code ("T2P 1J9", "M5V2T6"). */
const CA_POSTAL_CODE = /\b[A-Z]\d[A-Z] ?\d[A-Z]\d\b/i

/**
 * True for a City / State / Zip sample like "Portland, OR 97201": a comma, and after the first
 * comma a state code or a postal code. A city with no comma ("St. Louis") or with a country after
 * it ("Paris, France") is not flagged (#24975).
 */
function looksLikeCityStateZip(sample: string): boolean {
  const comma = sample.indexOf(',')
  if (comma === -1) {
    return false
  }

  const rest = sample.slice(comma + 1)
  return REGION_CODE.test(rest) || US_ZIP.test(rest) || CA_POSTAL_CODE.test(rest)
}

/** Fields where a full name packed into one column is flagged (#24975). */
const NAME_FIELDS = ['first_name', 'last_name']

/**
 * Source headers that name a whole-name column. Headers are normalized like the server's
 * `normalizeHeader`, so "Full_Name" and "Full-Name" match "full name".
 */
const FULL_NAME_HEADERS = ['name', 'full name', 'contact name']

/**
 * True for a First / Last Name column that holds a full name: a "Name" / "Full Name" header mapped
 * to First Name, or a sample with a comma ("Smith, John"). Spaces alone never flag — "Mary Ann"
 * and "Van Der Berg" are real single values (#24975).
 */
function looksLikeFullName(column: ImportColumn, field: string, sample: string | undefined): boolean {
  const header = (column.header ?? '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, ' ')
    .trim()
  if (field === 'first_name' && FULL_NAME_HEADERS.includes(header)) {
    return true
  }

  return !!sample?.includes(',')
}

/**
 * Banner summary. The singular text is the prototype's (plan U19); the row tooltip carries the
 * fix, so the two never repeat each other (#24975).
 */
export function getCombinedFieldBannerMessage(count: number): string {
  return count === 1
    ? 'One column looks like it holds more than one field — see the flagged row below.'
    : `${count} columns look like they hold more than one field — see the flagged rows below.`
}

/** Row tooltip: names what the column seems to combine, then says how to fix it. */
export function getCombinedFieldTooltip(field: string | null | undefined): string {
  if (field && NAME_FIELDS.includes(field)) {
    return 'This looks like a full name in one column. Split it into First Name and Last Name columns in your file, then upload it again.'
  }

  const parts = field ? COMBINED_HINT_PARTS[field] : undefined
  const what = parts ? `${parts} in one column` : 'more than one field in one column'

  return `This looks like ${what}. Split it into separate columns in your file, then upload it again.`
}

/**
 * Advisory heuristic (plan U19): flags columns mapped to Address whose sample reads like a whole
 * address line ("12 Main St, Springfield, IL, 62704") rather than a street, and columns mapped to
 * City, State / Province or Zip / Postal whose sample reads like "Portland, OR 97201", and First /
 * Last Name columns that hold a full name (#24975).
 *
 * The backend has no such detection, and this never blocks the wizard — it only shows a warning.
 */
export function getCombinedFieldColumns(columns: ImportColumn[], mapping: Record<number, string | null>): number[] {
  return (columns ?? [])
    .filter((column) => {
      const field = mapping[column.index]
      if (!field || !COMBINED_HINT_FIELDS.includes(field)) {
        return false
      }

      const sample = column.sample?.trim()
      if (NAME_FIELDS.includes(field)) {
        return looksLikeFullName(column, field, sample)
      }

      if (!sample) {
        return false
      }

      if (LOCALITY_FIELDS.includes(field)) {
        return looksLikeCityStateZip(sample)
      }

      const commas = (sample.match(/,/g) ?? []).length
      return commas >= COMBINED_HINT_MIN_COMMAS
    })
    .map((column) => column.index)
}
